"use client";

import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, ShieldCheck, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const trustTags = [{
  label: "Certified Metal Roof Installers"
}, {
  label: "Lifetime Material Warranty"
}, {
  label: "Built for Alberta"
}];
function RoofingQuoteForm() {
  const [formDef, setFormDef] = useState<any>(null);
  const [isLoadingDef, setIsLoadingDef] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: ""
  });
  const [hpValue, setHpValue] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setIsLoadingDef(false);
      setLoadError(true);
      return;
    }
    client.pluginRequest("wvc-forms/v1/forms/roofing_hero_quote_form_roofing").then((def: any) => {
      if (!isMounted) return;
      if (def && def.key) {
        setFormDef(def);
        setFetchTimestamp(Date.now());
        setLoadError(false);
      } else {
        setLoadError(true);
      }
    }).catch((err: any) => {
      if (!isMounted) return;
      console.error("Failed to load roofing quote form definition:", err);
      setLoadError(true);
    }).finally(() => {
      if (isMounted) {
        setIsLoadingDef(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);
  const handleInputChange = (field: "name" | "phone" | "email", val: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: val
    }));
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const updated = {
          ...prev
        };
        delete updated[field];
        return updated;
      });
    }
  };
  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      errors.name = "Please enter your name (at least 2 characters).";
    }
    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required.";
    }
    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        errors.email = "Please provide a valid email address.";
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validateForm()) {
      return;
    }
    if (!formDef) {
      setSubmitError("Quote form is not available at the moment. Please call us directly.");
      return;
    }

    // Minimum seconds spam protection check
    const minSeconds = formDef.spam?.min_seconds || 3;
    const elapsedSeconds = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSeconds < minSeconds) {
      const waitTimeMs = Math.ceil((minSeconds - elapsedSeconds) * 1000);
      await new Promise(resolve => setTimeout(resolve, waitTimeMs));
    }
    setIsSubmitting(true);
    try {
      const client = getWvcClient();
      if (!client?.pluginRequest) {
        throw new Error("Form client unavailable");
      }

      // Map field keys to IDs if available in definition
      const fields = Array.isArray(formDef.fields) ? formDef.fields : [];
      const nameField = fields.find((f: any) => f.key === "name");
      const phoneField = fields.find((f: any) => f.key === "phone");
      const emailField = fields.find((f: any) => f.key === "email");
      const nameId = nameField ? nameField.id : "f_name";
      const phoneId = phoneField ? phoneField.id : "f_phone";
      const emailId = emailField ? emailField.id : "f_email";
      const submitValues: Record<string, string> = {
        [nameId]: formData.name.trim(),
        [phoneId]: formData.phone.trim()
      };
      if (formData.email.trim()) {
        submitValues[emailId] = formData.email.trim();
      }
      const hpField = formDef.spam?.honeypot?.field;
      const res = await client.pluginRequest("wvc-forms/v1/forms/roofing_hero_quote_form_roofing/submit", {
        method: "POST",
        data: {
          values: submitValues,
          token: formDef.spam?.token,
          hp: hpField ? {
            [hpField]: hpValue
          } : undefined,
          meta: {
            source_url: typeof window !== "undefined" ? window.location.href : "",
            referrer: typeof document !== "undefined" ? document.referrer : ""
          }
        }
      });
      if (res && res.success) {
        setIsSubmitted(true);
        if (res.confirmation?.message) {
          setConfirmationMessage(res.confirmation.message);
        } else {
          setConfirmationMessage("Thank you! We've received your request. Our roofing team will contact you shortly.");
        }
      } else if (res && res.code === "frms_validation_failed" && res.errors) {
        const newErrors: Record<string, string> = {};
        if (res.errors[nameId]) newErrors.name = res.errors[nameId].message;
        if (res.errors[phoneId]) newErrors.phone = res.errors[phoneId].message;
        if (res.errors[emailId]) newErrors.email = res.errors[emailId].message;
        setFieldErrors(newErrors);
        setSubmitError(res.message || "Please check the highlighted fields.");
      } else {
        setSubmitError(res?.message || "Submission failed. Please call us directly at (587) 888-4223.");
      }
    } catch (err: any) {
      console.error("Quote submission error:", err);
      setSubmitError("Unable to send quote request right now. Please call us directly at (587) 888-4223.");
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isLoadingDef) {
    return <div className="py-12 flex flex-col items-center justify-center text-center space-y-3">
        <div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
          Loading quote form...
        </p>
      </div>;
  }
  if (loadError) {
    return <div className="rounded-md border border-border bg-muted/40 p-5 space-y-3">
        <div className="flex items-start gap-2.5">
          <AlertCircle className="size-5 text-primary shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-foreground">
              Quote Form Temporarily Unavailable
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Our instant quote form is currently unavailable. Please call our Calgary roofing specialists directly at{" "}
              <a href="tel:+15878884223" className="font-semibold text-primary underline underline-offset-2 hover:text-primary/90">
                (587) 888-4223
              </a>{" "}
              to discuss your project or request your free roofing estimate.
            </p>
          </div>
        </div>
      </div>;
  }
  if (isSubmitted) {
    return <div className="rounded-md border border-primary/40 bg-primary/10 p-5 space-y-2">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="size-4 text-primary shrink-0" />
          <h3 className="font-semibold text-foreground text-sm">Quote Requested</h3>
        </div>
        {confirmationMessage.startsWith("<") ? <div className="text-muted-foreground text-xs leading-relaxed" dangerouslySetInnerHTML={{
        __html: confirmationMessage
      }} /> : <p className="text-muted-foreground text-xs leading-relaxed">
            {confirmationMessage}
          </p>}
      </div>;
  }
  return <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {submitError && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 flex items-start gap-2">
          <AlertCircle className="size-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-xs text-destructive leading-normal">{submitError}</p>
        </div>}

      {/* Honeypot Decoy */}
      {formDef?.spam?.honeypot?.field && <input type="text" name={formDef.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} tabIndex={-1} autoComplete="off" style={{
      position: "absolute",
      left: "-9999px"
    }} aria-hidden="true" />}

      {/* Name Field */}
      <div className="space-y-1.5">
        <label htmlFor="roofing-hero-name" className="block text-xs font-medium uppercase tracking-wider text-card-foreground font-mono">
          Your Name <span className="text-primary">*</span>
        </label>
        <input id="roofing-hero-name" type="text" autoComplete="name" value={formData.name} onChange={e => handleInputChange("name", e.target.value)} placeholder="e.g. John Miller" className="h-11 w-full rounded-sm border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors" />
        {fieldErrors.name && <p className="text-xs text-destructive font-default mt-1">
            {fieldErrors.name}
          </p>}
      </div>

      {/* Phone Field */}
      <div className="space-y-1.5">
        <label htmlFor="roofing-hero-phone" className="block text-xs font-medium uppercase tracking-wider text-card-foreground font-mono">
          Phone Number <span className="text-primary">*</span>
        </label>
        <input id="roofing-hero-phone" type="tel" autoComplete="tel" value={formData.phone} onChange={e => handleInputChange("phone", e.target.value)} placeholder="e.g. (403) 555-0192" className="h-11 w-full rounded-sm border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors" />
        {fieldErrors.phone && <p className="text-xs text-destructive font-default mt-1">
            {fieldErrors.phone}
          </p>}
      </div>

      {/* Email Field */}
      <div className="space-y-1.5">
        <label htmlFor="roofing-hero-email" className="block text-xs font-medium uppercase tracking-wider text-card-foreground font-mono">
          Email Address{" "}
          <span className="font-normal normal-case text-muted-foreground ml-1 text-[10px]">
            (Optional)
          </span>
        </label>
        <input id="roofing-hero-email" type="email" autoComplete="email" value={formData.email} onChange={e => handleInputChange("email", e.target.value)} placeholder="e.g. john@example.com" className="h-11 w-full rounded-sm border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors" />
        {fieldErrors.email && <p className="text-xs text-destructive font-default mt-1">
            {fieldErrors.email}
          </p>}
      </div>

      {/* Privacy note */}
      <p className="text-[10px] text-muted-foreground leading-normal font-default">
        We use the details you submit to respond to your inquiry and prepare your quote. Read our{" "}
        <Link to="#" className="underline underline-offset-2 text-foreground hover:text-primary transition-colors">
          Privacy Policy
        </Link>
        .
      </p>

      {/* Submit button */}
      <div className="pt-2">
        <Button type="submit" disabled={isSubmitting} className="w-full h-11 uppercase font-bold tracking-wide rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-xs">
          {isSubmitting ? "Submitting..." : "REQUEST A FREE QUOTE"}
        </Button>
      </div>

      {/* Reassurance text */}
      <div className="flex items-center justify-center gap-4 pt-2 text-xs text-muted-foreground font-mono">
        <span className="inline-flex items-center gap-1">
          <ShieldCheck className="size-3.5 text-primary shrink-0" />
          No obligation
        </span>
        <span>•</span>
        <span className="inline-flex items-center gap-1">
          <Clock className="size-3.5 text-primary shrink-0" />
          Fast response
        </span>
      </div>
    </form>;
}
export default function RoofingHero() {
  const prefersReducedMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  const containerVariant = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1
      }
    }
  };
  return <section id="roofing-hero" data-nav="light" className="relative bg-background text-foreground py-10 md:py-16 lg:py-20 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          
          {/* Left Column (Roughly 7/12 on Desktop) */}
          <motion.div className="lg:col-span-7 flex flex-col gap-6" variants={containerVariant} initial="hidden" animate="visible">
            {/* Eyebrow */}
            <motion.div variants={fadeUpVariant} className="inline-flex items-center gap-2">
              <span className="text-xs font-mono font-semibold uppercase tracking-widest text-primary bg-primary/10 px-2.5 py-1 rounded-sm inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-primary inline-block" />
                CALGARY METAL ROOFING
              </span>
            </motion.div>

            {/* Main Heading H1 */}
            <motion.h1 variants={fadeUpVariant} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-[1.15]">
              Metal Roofing Supply &amp; Installation in Calgary
            </motion.h1>

            {/* Supporting Paragraph */}
            <motion.p variants={fadeUpVariant} className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-default">
              We supply and professionally install premium metal roofing systems for residential and commercial properties across Calgary and the surrounding areas. Engineered for Alberta&apos;s toughest conditions, our roofing systems provide exceptional durability, energy efficiency, and timeless architectural appeal.
            </motion.p>

            {/* Supply & Installation Statement Callout */}
            <motion.div variants={fadeUpVariant} className="p-3.5 sm:p-4 rounded-md bg-muted/60 border border-border flex items-center gap-3 max-w-2xl">
              <AlertCircle className="size-5 text-primary shrink-0" />
              <p className="text-xs sm:text-sm font-bold text-foreground leading-snug">
                Supply &amp; installation projects only - roofing materials are not sold separately.
              </p>
            </motion.div>

            {/* Trust Tags & Call Action */}
            <motion.div variants={fadeUpVariant} className="flex flex-wrap items-center gap-2.5 pt-1">
              {trustTags.map((tag, i) => <div key={tag.label} data-index={i}>
                  <Badge variant="outline" className="border-border bg-muted/60 text-foreground text-xs uppercase tracking-wide py-1.5 px-3 font-medium flex items-center gap-1.5 rounded-sm shadow-2xs">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>{tag.label}</span>
                  </Badge>
                </div>)}

              <a href="tel:+15878884223" className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold uppercase tracking-wider text-foreground hover:text-primary transition-colors py-1.5 px-3 rounded-sm border border-border bg-background hover:bg-muted shadow-2xs">
                <Phone className="size-3 text-primary shrink-0" />
                <span>Call (587) 888-4223</span>
              </a>
            </motion.div>

            {/* Featured Roofing Image */}
            <motion.div variants={fadeUpVariant} className="relative mt-2 overflow-hidden rounded-md border border-border bg-muted aspect-16/10 shadow-sm">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/standing_seam_roof.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/standing_seam_roof.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1713190/img/standing_seam_roof.png-wvc-sizes" alt="Professional standing seam metal roof installation in Calgary" loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </motion.div>
          </motion.div>

          {/* Right Column (Roughly 5/12 on Desktop) */}
          <motion.div className="lg:col-span-5" initial={prefersReducedMotion ? {} : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: prefersReducedMotion ? 0 : 0.15,
          ease: "easeOut"
        }}>
            <div className="bg-card text-card-foreground border border-border rounded-md shadow-md p-6 sm:p-8">
              {/* Form Header */}
              <div className="border-b border-border pb-5 mb-6">
                <span className="text-xs font-mono uppercase tracking-widest text-primary font-semibold block mb-1">
                  Free Estimate
                </span>
                <h2 className="text-xl sm:text-2xl font-default font-bold text-card-foreground tracking-tight uppercase">
                  Get a free roofing quote
                </h2>
                <p className="text-sm text-muted-foreground mt-1.5 leading-normal font-default">
                  Get a free estimate for your metal roofing project. We&apos;ll get back to you shortly.
                </p>
              </div>

              {/* Roofing Quote Form */}
              <RoofingQuoteForm />
            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}